import React from 'react';
import styled from '@emotion/styled';
import { Text, devices } from '../common';
import { QuickLink } from '../../@types/types';
import { isExternalUrl, resolveContentUrl } from '../../utils/url';

type Props = {
  title?: string;
  links: QuickLink[];
};

const QuickLinksSection = ({ title, links }: Props) => {
  if (!links || links.length === 0) return null;

  return (
    <Container>
      <Inner>
        {title && (
          <Heading variant="title-3" color="white">
            {title}
          </Heading>
        )}
        <LinksRow>
          {links.map(({ Title, Url }) => {
            const href = resolveContentUrl(Url);
            return (
              <LinkButton
                key={`${Title}-${Url}`}
                href={href}
                {...(isExternalUrl(href)
                  ? { target: '_blank', rel: 'noopener noreferrer' }
                  : {})}
              >
                {Title}
              </LinkButton>
            );
          })}
        </LinksRow>
      </Inner>
    </Container>
  );
};

export default QuickLinksSection;

const Container = styled.section`
  background-color: ${(props) => props.theme.colors.darkBlue};
  padding: 40px 0 48px;
`;

const Inner = styled.div`
  width: 90%;
  max-width: 1100px;
  margin: 0 auto;
`;

const Heading = styled(Text)`
  text-align: center;
  margin-bottom: 28px;
  max-width: none;
`;

const LINKS_GAP = 12;

const LinksRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${LINKS_GAP}px;
`;

const LinkButton = styled.a`
  /* Fixed basis keeps the 4-up row rhythm, so a partial row centers instead of
     stretching the cards. */
  flex: 0 1 calc((100% - ${LINKS_GAP * 3}px) / 4);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 52px;
  padding: 12px 16px;
  border-radius: ${(props) => props.theme.spacing.default};
  background-color: ${(props) => props.theme.colors.white};
  color: ${(props) => props.theme.colors.darkBlue};
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  text-align: center;
  line-height: 1.3;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${(props) => props.theme.colors.lightestBlue};
  }

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.focus};
    outline-offset: 2px;
  }

  @media ${devices.laptop} {
    flex-basis: calc((100% - ${LINKS_GAP}px) / 2);
  }

  @media ${devices.mobileXL} {
    flex-basis: 100%;
  }
`;
